/* ================================================================
   Reference Mega Header V6.5 PATCH
   1. The scrolled header is repainted with inline !important styles so a
      theme or app stylesheet can never leave it transparent, and the
      MEN / WOMEN / JUNIORS row is removed until the page is back at the top.
   2. A transparent header writes those three departments in white.
   3. The blur + shadow behind the open hamburger menu covers the whole
      viewport; the white menu panel and the top bar stay clean above it.
   ================================================================ */
(() => {
  if (window.__refV65Installed) return;
  window.__refV65Installed = true;

  const INK_DARK = '#111';
  const INK_WHITE = '#fff';
  const isDesktop = () => window.matchMedia('(min-width:768px)').matches;
  const put = (el, prop, value) => { if (el) el.style.setProperty(prop, value, 'important'); };
  const drop = (el, prop) => { if (el) el.style.removeProperty(prop); };

  /* ---------- 1 + 2: the header bar ---------- */

  const inkNodes = (root) => [
    ...root.querySelectorAll(
      '.ref-header__bar .ref-header__tab,' +
      '.ref-header__bar .ref-search-trigger,' +
      '.ref-header__bar .ref-search-trigger span,' +
      '.ref-header__bar .ref-icon-btn,' +
      '.ref-header__bar .ref-menu-trigger'
    ),
  ];

  const paintHeader = (root) => {
    if (!root) return;
    const bar = root.querySelector('.ref-header__bar');
    if (!bar) return;
    const tabs = root.querySelector('.ref-header__tabs');
    const scrolled = root.classList.contains('ref-scroll-sticky');
    const transparent = root.classList.contains('ref-header--transparent');
    const ink = scrolled || !transparent ? INK_DARK : INK_WHITE;

    put(bar, 'background-image', 'none');
    put(bar, 'background-color', scrolled || !transparent ? INK_WHITE : 'transparent');
    put(bar, 'color', ink);
    inkNodes(root).forEach((el) => put(el, 'color', ink));

    /* The department row belongs to the very top of the page only. */
    if (scrolled) put(tabs, 'display', 'none');
    else drop(tabs, 'display');
  };

  /* The closed hamburger must never paint its own MEN / WOMEN / JUNIORS row
     over the page. Inline styles keep that true even if the rule is stripped. */
  const paintMegaTabs = (root, open) => {
    const tabs = root.querySelector('.ref-mega-tabs');
    if (!tabs) return;
    put(tabs, 'visibility', open ? 'visible' : 'hidden');
    put(tabs, 'opacity', open ? '1' : '0');
    put(tabs, 'pointer-events', open ? 'auto' : 'none');
  };

  /* ---------- 3: full-screen blur behind the hamburger ---------- */

  const paintMegaShade = (root, on) => {
    const shade = root.querySelector('.ref-page-preview-shade');
    const content = root.querySelector('.ref-mega-content');
    const head = root.querySelector('.ref-mega-head');

    if (!isDesktop()) {
      if (shade) shade.removeAttribute('style');
      [content, head].forEach((el) => {
        if (!el) return;
        el.style.removeProperty('z-index');
        el.style.removeProperty('position');
      });
      return;
    }

    /* The white panel and the top bar always paint above the blur. */
    put(content, 'position', 'relative');
    put(content, 'z-index', '2');
    put(content, 'background-color', '#fff');
    put(head, 'position', 'relative');
    put(head, 'z-index', '3');
    put(head, 'background-color', '#fff');

    if (!shade) return;
    put(shade, 'position', 'fixed');
    put(shade, 'top', '0');
    put(shade, 'left', '0');
    put(shade, 'right', '0');
    put(shade, 'bottom', '0');
    put(shade, 'width', '100vw');
    put(shade, 'height', '100vh');
    put(shade, 'z-index', '1');
    put(shade, 'display', 'block');
    put(shade, 'pointer-events', 'none');
    put(shade, 'background-color', 'rgba(10,10,10,0.13)');
    put(shade, 'background-image', 'none');
    put(shade, '-webkit-backdrop-filter', 'blur(7px) brightness(0.78)');
    put(shade, 'backdrop-filter', 'blur(7px) brightness(0.78)');
    put(shade, 'transition', on ? 'none' : 'opacity .16s ease');
    put(shade, 'opacity', on ? '1' : '0');
    /* Visibility is deliberately left to .ref-mega-layer so the shade can still
       fade out during the 0.18s the closing layer stays on screen. */
  };

  /* ---------- wiring ---------- */

  const bind = (root) => {
    if (!root || root.dataset.refV65 === '1') return;
    root.dataset.refV65 = '1';

    const mega = root.querySelector('.ref-mega-layer');
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        paintHeader(root);
        if (mega) {
          const open = mega.classList.contains('is-open');
          paintMegaTabs(root, open);
          paintMegaShade(root, open);
        }
      });
    };

    schedule();

    new MutationObserver(schedule).observe(root, {
      attributes: true,
      attributeFilter: ['class'],
    });
    if (mega) {
      new MutationObserver(schedule).observe(mega, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, { passive: true });
  };

  const boot = () => document.querySelectorAll('.ref-header').forEach(bind);
  boot();
  document.addEventListener('DOMContentLoaded', boot);
  window.addEventListener('load', boot);
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.matches?.('.ref-header')
      ? event.target
      : event.target?.querySelector?.('.ref-header');
    bind(root);
  });
})();

/* ================================================================
   Reference Mega Header V6.6 PATCH
   The department preview on the right of the open hamburger is an <iframe>
   pointing at the real page. A host can refuse to be framed
   (X-Frame-Options / Content-Security-Policy: frame-ancestors), and a refused
   frame paints as a flat grey rectangle — exactly what the preview turned into
   after clicking MEN / WOMEN / JUNIORS.

   Fetching the page is never refused, because it is same-origin. So when
   framing is blocked the page is fetched and written straight into the frame.
   The theme's own scripts are kept, so the lazy images load and the banner
   slideshow opens exactly as on the real page; only trackers, chat/popup apps
   and this header itself are removed, and the page's own header is hidden so
   the preview never shows a second copy of it.
   ================================================================ */
(() => {
  if (window.__refV66Installed) return;
  window.__refV66Installed = true;

  const BLANK = 'about:blank';
  const pages = new Map();
  let framingBlocked = false;

  const sameOrigin = (url) => {
    try { return new URL(url, location.href).origin === location.origin; }
    catch (_) { return false; }
  };

  /* A refused frame exposes no reachable document at all. */
  const readableDoc = (frame) => {
    try {
      const doc = frame.contentDocument;
      return doc && doc.body && doc.body.children.length ? doc : null;
    } catch (_) {
      return null;
    }
  };

  const PREVIEW_CSS =
    'html,body{background:#fff!important;opacity:1!important;visibility:visible!important;overflow:hidden!important}' +
    '.reference-mega-header-section,.ref-header,.ref-cart-toast{display:none!important}' +
    '#pageloading,.page-loading,.loading-page,.preloader,#preloader,.pace,' +
    '.pace-progress,.cookie-banner,.newsletter-popup,.modal-backdrop{display:none!important}';

  const addPreviewCss = (doc) => {
    if (!doc || doc.getElementById('ref-v66-preview-css')) return;
    const style = doc.createElement('style');
    style.id = 'ref-v66-preview-css';
    style.textContent = PREVIEW_CSS;
    (doc.head || doc.documentElement)?.appendChild(style);
  };

  /* The theme's own scripts must run inside the preview, otherwise the lazy
     loader never swaps the placeholder images and the banner slideshow never
     opens - the preview then shows a page of empty grey boxes. Only trackers,
     chat/popup apps and this header itself are removed. */
  const DROP_SCRIPT = new RegExp(
    'reference-mega-header|web-pixels-manager|web_pixels|trekkie|monorail|' +
    'shopify/features|consent-tracking|google-analytics|googletagmanager|gtag/js|' +
    'connect\\.facebook|fbevents|hotjar|clarity\\.ms|tiktok|analytics\\.tiktok|' +
    'snap\\.licdn|pinimg|criteo|klaviyo|tidio|tawk|intercom|zdassets|' +
    'gorgias|pushowl|privy|omnisend|smile\\.io',
    'i'
  );
  const DROP_INLINE = /\b(gtag|fbq|ttq|dataLayer\.push|trekkie|webPixelsManager|ga\()\s*\(?/i;

  const prepare = (html, src) => {
    let out = String(html);
    out = out.replace(/<script\b([^>]*)>([\s\S]*?)<\/script>/gi, (tag, attrs, body) => {
      if (DROP_SCRIPT.test(attrs) || DROP_SCRIPT.test(body)) return '';
      if (!/\bsrc\s*=/i.test(attrs) && DROP_INLINE.test(body)) return '';
      return tag;
    });
    out = out.replace(/<script\b[^>]*\/>/gi, (tag) => (DROP_SCRIPT.test(tag) ? '' : tag));
    out = out.replace(/<base\b[^>]*>/gi, '');
    const baseTag = '<base href="' + src.replace(/"/g, '&quot;') + '">';
    if (/<head[^>]*>/i.test(out)) out = out.replace(/<head([^>]*)>/i, '<head$1>' + baseTag);
    else out = baseTag + out;
    return out;
  };

  /* Safety net for whatever the theme's own loader has not reached yet.
     Shopify keeps a {width} placeholder inside those URLs, so it is filled in
     rather than left to 404 into an empty grey box. */
  const realUrl = (value) => (value || '').replace(/\{width\}/g, '1200').replace(/\{size\}/g, '1200x');

  const unlazy = (doc) => {
    doc.querySelectorAll('img[data-src],source[data-src]').forEach((el) => {
      const value = realUrl(el.getAttribute('data-src'));
      const current = el.getAttribute('src') || '';
      if (value && (!current || current.indexOf('{width}') >= 0 || /^data:image\/(gif|svg)/.test(current))) {
        el.setAttribute('src', value);
      }
    });
    doc.querySelectorAll('img[data-srcset],source[data-srcset]').forEach((el) => {
      const value = realUrl(el.getAttribute('data-srcset'));
      if (value) el.setAttribute('srcset', value);
    });
    doc.querySelectorAll('img[src*="{width}"],source[src*="{width}"]').forEach((el) => {
      el.setAttribute('src', realUrl(el.getAttribute('src')));
    });
    doc.querySelectorAll('[data-bg],[data-bgset]').forEach((el) => {
      const value = realUrl(el.getAttribute('data-bg') || (el.getAttribute('data-bgset') || '').split(/[\s,]+/)[0]);
      if (value) el.style.backgroundImage = 'url("' + value + '")';
    });
    doc.querySelectorAll('img[loading="lazy"]').forEach((el) => el.setAttribute('loading', 'eager'));
  };

  const load = (src) => {
    if (pages.has(src)) return pages.get(src);
    const request = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 9000);
      try {
        const res = await fetch(src, { credentials: 'same-origin', signal: controller.signal });
        if (!res.ok) throw new Error(String(res.status));
        return await res.text();
      } finally {
        clearTimeout(timer);
      }
    })().catch(() => '');
    pages.set(src, request);
    return request;
  };

  const blank = (frame) => new Promise((resolve) => {
    let settled = false;
    const done = () => { if (!settled) { settled = true; resolve(); } };
    frame.addEventListener('load', done, { once: true });
    /* Scripts may run, but the preview may never navigate the real tab, open a
       window or submit anything. */
    frame.setAttribute('sandbox', 'allow-scripts allow-same-origin');
    frame.removeAttribute('src');
    frame.src = BLANK;
    setTimeout(done, 900);
  });

  const inject = async (frame, src) => {
    if (!sameOrigin(src)) return;
    if (frame.dataset.refV66For === src) return;
    frame.dataset.refV66For = src;

    const html = await load(src);
    if (!html) { frame.dataset.refV66For = ''; pages.delete(src); return; }
    if (frame.dataset.refV66For !== src) return;

    await blank(frame);
    if (frame.dataset.refV66For !== src) return;

    let doc = null;
    try { doc = frame.contentDocument; } catch (_) { doc = null; }
    if (!doc) { frame.dataset.refV66For = ''; return; }

    try {
      doc.open();
      doc.write(prepare(html, src));
      doc.close();
      addPreviewCss(doc);
      unlazy(doc);
      /* The theme's loader fills images in over the next moments; these passes
         catch anything it leaves behind. */
      [120, 400, 900, 1800].forEach((delay) => setTimeout(() => {
        if (frame.dataset.refV66For !== src) return;
        try { unlazy(frame.contentDocument); addPreviewCss(frame.contentDocument); } catch (_) {}
      }, delay));
    } catch (_) {
      frame.dataset.refV66For = '';
      return;
    }

    /* Keep the buffer marked the way the menu expects it. */
    frame.dataset.refLoaded = '1';
    frame.classList.remove('is-loading');
  };

  const hook = (frame) => {
    if (frame.dataset.refV66Hooked === '1') return;
    frame.dataset.refV66Hooked = '1';

    frame.addEventListener('load', () => {
      const src = frame.getAttribute('src') || '';
      if (!src || src === BLANK) return;
      const doc = readableDoc(frame);
      if (doc) {
        frame.dataset.refV66For = '';
        unlazy(doc);
        addPreviewCss(doc);
        return;
      }
      framingBlocked = true;
      inject(frame, src);
    });

    /* Once this shop is known to refuse framing, the fetch starts the moment a
       department is chosen instead of waiting for the frame to fail again. */
    new MutationObserver(() => {
      const src = frame.getAttribute('src') || '';
      if (!src || src === BLANK || !sameOrigin(src)) return;
      load(src);
      if (framingBlocked) inject(frame, src);
    }).observe(frame, { attributes: true, attributeFilter: ['src'] });
  };

  const scan = () => document.querySelectorAll('.ref-v57-live-frame').forEach(hook);

  scan();
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();

/* ================================================================
   Reference Mega Header V6.7 PATCH
   The theme's "Thanks for subscribing!" success popup
   (snippets/halo-newsletter-success-popup, #newsletter-success-modal) opens
   after any newsletter signup but its X button has no working handler, so
   it could not be closed. The X, a click outside the box and Escape now all
   close it. A later signup on the same page can still open it again.
   ================================================================ */
(() => {
  if (window.__refV67Installed) return;
  window.__refV67Installed = true;

  const MODAL = '#newsletter-success-modal, [data-newsletter-success-modal]';
  const OPEN_CLASSES = ['is-open', 'is-active', 'is-visible', 'active', 'show', 'open', 'visible'];

  const isShown = (modal) => {
    if (!modal) return false;
    const style = getComputedStyle(modal);
    return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0.05;
  };

  const hide = (modal) => {
    if (!modal) return;
    OPEN_CLASSES.forEach((name) => modal.classList.remove(name));
    modal.setAttribute('aria-hidden', 'true');
    modal.style.setProperty('display', 'none', 'important');
    /* Body state classes the theme may have added for this popup only. */
    [...document.body.classList]
      .filter((name) => /newsletter/i.test(name) && /(success|message)/i.test(name))
      .forEach((name) => document.body.classList.remove(name));
  };

  document.addEventListener('click', (event) => {
    const modal = document.querySelector(MODAL);
    if (!modal || !isShown(modal)) return;
    const closeButton = event.target.closest?.('.close, .close-modal, [data-close], [aria-label="Close"]');
    const box = modal.querySelector('.halo-popup-wrapper') || modal;
    if (closeButton && modal.contains(closeButton)) {
      event.preventDefault();
      hide(modal);
    } else if (!box.contains(event.target)) {
      /* Outside the box: close it, but let the click do its own job too. */
      hide(modal);
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const modal = document.querySelector(MODAL);
    if (modal && isShown(modal)) hide(modal);
  });

  /* A new signup must be allowed to show the popup again. */
  document.addEventListener('submit', (event) => {
    if (!event.target?.querySelector?.('[name="contact[email]"]')) return;
    document.querySelector(MODAL)?.style.removeProperty('display');
  }, true);

  window.refHideNewsletterSuccess = () => hide(document.querySelector(MODAL));
})();